package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.Profile
import com.dotdrives.repository.OrgRepository
import com.dotdrives.repository.ProfileRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

data class InviteDraft(
    val email: String,
    val role: String,
)

data class OnboardingUiState(
    val step: Int = 1,
    val firstName: String = "",
    val lastName: String = "",
    val phone: String = "",
    val orgName: String = "",
    val orgKind: String = "nonprofit",
    val createdOrgId: String? = null,
    val invites: List<InviteDraft> = emptyList(),
    val importedGiverCount: Int? = null,
    val finished: Boolean = false,
    val loading: Boolean = false,
    val error: String? = null,
) {
    val canSubmitProfile: Boolean
        get() = firstName.isNotBlank() && lastName.isNotBlank() && phone.isNotBlank()

    val canSubmitOrg: Boolean
        get() = orgName.isNotBlank() && orgKind in ORG_KINDS

    val eligibleInviteCount: Int
        get() = invites.count(InviteDraft::isEligible)

    val canSubmitInvites: Boolean
        get() = invites.isNotEmpty() && eligibleInviteCount == invites.size
}

class OnboardingController(
    private val orgRepo: OrgRepository,
    private val profileRepo: ProfileRepository,
    scope: CoroutineScope,
) : DotController<OnboardingUiState>(OnboardingUiState(), scope) {
    private var createdOrgId: String? = null
    private val persistedInviteEmails = mutableSetOf<String>()

    fun setProfile(first: String, last: String, phone: String) {
        update {
            it.copy(
                firstName = first,
                lastName = last,
                phone = phone,
                error = null,
            )
        }
    }

    fun submitProfile() {
        val current = state.value
        if (current.step != PROFILE_STEP || current.loading) return
        if (!current.canSubmitProfile) {
            update { it.copy(error = "First name, last name, and phone are required") }
            return
        }
        update { it.copy(loading = true, error = null) }
        scope.launch {
            profileRepo.upsert(
                Profile(
                    userId = "",
                    firstName = current.firstName,
                    lastName = current.lastName,
                    phone = current.phone,
                ),
            ).onSuccess {
                update { it.copy(step = ORG_STEP, loading = false) }
            }.onFailure {
                publishError(it)
            }
        }
    }

    fun setOrg(name: String, kind: String) {
        update {
            it.copy(
                orgName = name,
                orgKind = kind,
                error = null,
            )
        }
    }

    fun submitOrg() {
        val current = state.value
        if (current.step != ORG_STEP || current.loading) return
        if (!current.canSubmitOrg) {
            update { it.copy(error = "Organization name and type are required") }
            return
        }
        update { it.copy(loading = true, error = null) }
        scope.launch {
            val existingOrgId = createdOrgId
            val result = if (existingOrgId == null) {
                orgRepo.createOrg(current.orgName, current.orgKind)
            } else {
                orgRepo.updateOrg(existingOrgId, current.orgName, current.orgKind)
            }
            result
                .onSuccess { org ->
                    if (existingOrgId == null) {
                        createdOrgId = org.id
                        persistedInviteEmails.clear()
                    }
                    update {
                        it.copy(
                            step = INVITES_STEP,
                            createdOrgId = org.id,
                            loading = false,
                        )
                    }
                }
                .onFailure {
                    publishError(it)
                }
        }
    }

    fun addInvite(email: String, role: String) {
        if (role !in INVITE_ROLES) {
            update { it.copy(error = "Invite role must be admin, member, or field") }
            return
        }
        update {
            it.copy(
                invites = it.invites + InviteDraft(email, role),
                error = null,
            )
        }
    }

    fun updateInvite(index: Int, email: String, role: String) {
        if (role !in INVITE_ROLES) {
            update { it.copy(error = "Invite role must be admin, member, or field") }
            return
        }
        update { current ->
            if (index !in current.invites.indices) {
                current
            } else {
                current.copy(
                    invites = current.invites.mapIndexed { inviteIndex, invite ->
                        if (inviteIndex == index) InviteDraft(email, role) else invite
                    },
                    error = null,
                )
            }
        }
    }

    fun removeInvite(index: Int) {
        update { current ->
            if (index !in current.invites.indices) {
                current
            } else {
                current.copy(
                    invites = current.invites.filterIndexed { inviteIndex, _ ->
                        inviteIndex != index
                    },
                    error = null,
                )
            }
        }
    }

    fun submitInvites() {
        val current = state.value
        if (current.step != INVITES_STEP || current.loading) return
        if (!current.canSubmitInvites) {
            update { it.copy(error = "Enter a valid email and role for every invite") }
            return
        }
        val orgId = createdOrgId
        if (orgId == null) {
            update { it.copy(error = "Create an organization before inviting teammates") }
            return
        }
        update { it.copy(loading = true, error = null) }
        scope.launch {
            for (invite in current.invites) {
                val normalizedEmail = invite.email.trim().lowercase()
                if (normalizedEmail in persistedInviteEmails) continue
                val result = orgRepo.invite(
                    orgId = orgId,
                    email = normalizedEmail,
                    role = invite.role,
                )
                if (result.isFailure) {
                    publishError(requireNotNull(result.exceptionOrNull()))
                    return@launch
                }
                persistedInviteEmails += normalizedEmail
            }
            update { it.copy(step = GIVERS_STEP, loading = false) }
        }
    }

    fun skipStep() {
        update { current ->
            when (current.step) {
                PROFILE_STEP -> current.copy(step = ORG_STEP, error = null)
                ORG_STEP -> current.copy(step = INVITES_STEP, error = null)
                INVITES_STEP -> current.copy(step = GIVERS_STEP, error = null)
                GIVERS_STEP -> current.copy(finished = true, error = null)
                else -> current
            }
        }
    }

    fun completeGiverImport(importedCount: Int) {
        if (state.value.step != GIVERS_STEP || importedCount < 0) return
        update {
            it.copy(
                importedGiverCount = importedCount,
                finished = true,
                loading = false,
                error = null,
            )
        }
    }

    fun back() {
        update { current ->
            when {
                current.finished -> current.copy(finished = false, error = null)
                current.step > PROFILE_STEP -> current.copy(
                    step = current.step - 1,
                    error = null,
                )
                else -> current
            }
        }
    }

    private fun publishError(error: Throwable) {
        if (error is CancellationException) {
            throw error
        }
        update {
            it.copy(
                loading = false,
                error = error.message ?: "Something went wrong",
            )
        }
    }

    private companion object {
        const val PROFILE_STEP = 1
        const val ORG_STEP = 2
        const val INVITES_STEP = 3
        const val GIVERS_STEP = 4
    }
}

private val ORG_KINDS = setOf("nonprofit", "ministry", "school", "other")
private val INVITE_ROLES = setOf("admin", "member", "field")

private fun InviteDraft.isEligible(): Boolean {
    val normalizedEmail = email.trim()
    val atIndex = normalizedEmail.indexOf('@')
    return role in INVITE_ROLES &&
        atIndex > 0 &&
        atIndex == normalizedEmail.lastIndexOf('@') &&
        atIndex < normalizedEmail.lastIndex &&
        normalizedEmail.none(Char::isWhitespace)
}
